import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { supabase } from '@/integrations/supabase/client';
import {
  CONTRATO_PDF_LIMPEZA,
  CONTRATO_TEMPLATES_BUCKET,
  planoIncluiLimpeza,
  resolverTemplateContrato,
} from '@/lib/planoLimpeza';

type CoordCampo = { x: number; y: number; page?: number; /** Largura do blank; se definida, o texto é centralizado (e reduzido se não couber) */ width?: number };

interface DadosContrato {
  cliente: {
    nome: string;
    cpf_cnpj?: string;
    endereco?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
    telefone?: string;
    email?: string;
  };
  equipamentos?: Array<{
    equipamento?: {
      marca?: string;
      modelo?: string;
      potencia?: string;
      tipo?: string;
    };
    quantidade: number;
  }>;
  plano?: {
    nome?: string;
    tipo_plano?: string;
    vigencia_anos?: number;
    inclui_limpeza?: boolean;
  };
  orcamento?: {
    quantidade_placas?: number;
    valor_seguro?: number;
    valor_total?: number;
    forma_pagamento?: string;
    vigencia_anos?: number;
  };
  projeto?: {
    id_projeto_elevo?: string;
  };
  contrato?: {
    data_inicio: string;
    data_fim: string;
    valor: number;
  };
}

/**
 * Coordenadas dos campos no PDF do contrato (templates de 13 páginas).
 *
 * COMO ATUALIZAR AS COORDENADAS:
 * 1. Abra descobrir-campos-contrato.html (ex.: http://localhost:8080/descobrir-campos-contrato.html)
 * 2. Carregue o PDF do projeto e clique onde cada campo deve aparecer
 * 3. Substitua os objetos abaixo pelo código gerado
 *
 * Páginas: 1 (cliente), 2 (equipamentos), 4 (preço), 15 (data + dados do cliente na assinatura).
 */
const coordenadasContratoNormal: Record<string, CoordCampo> = {
  // Página 1 - Dados do cliente
  nome_cliente: { x: 169, y: 703.4, page: 1 },
  cpf_cnpj: { x: 140, y: 665.8, page: 1 },

  // Página 2 - Dados do equipamento (PAINÉIS / Qtd / INVERSOR / Qtd / ESTRUTURA / Qtd)
  marca_paineis: { x: 128.5, y: 477.4, page: 2 },
  modelo_paineis: { x: 232, y: 477.4, page: 2 },
  quantidade_paineis: { x: 103, y: 448.6, page: 2 },
  marca_inversores: { x: 141, y: 429.8, page: 2 },
  modelo_inversores: { x: 293, y: 429.8, page: 2 },
  quantidade_inversores: { x: 103, y: 401.0, page: 2 },
  marca_estrutura: { x: 150, y: 382.2, page: 2 },
  modelo_estrutura: { x: 293, y: 382.2, page: 2 },
  quantidade_estrutura: { x: 103, y: 353.4, page: 2 },

  // Página 4 - CLÁUSULA TERCEIRA: "(_____); (_____)" — valor / forma de pagamento centralizados
  valor_seguro: { x: 75, y: 650.1, page: 4, width: 110 },
  forma_pagamento: { x: 217, y: 650.1, page: 4, width: 213 },

  // Página 15 - "Maringá,            ." — data por extenso centralizada no espaço
  data_local: { x: 269.5, y: 619.4, page: 15, width: 118 },
  // Página 15 - abaixo da linha de assinatura do CONTRATANTE (centralizado na linha)
  nome_cliente_assinatura: { x: 190.35, y: 425.8, page: 15, width: 229.1 },
  cpf_cnpj_assinatura: { x: 190.35, y: 411.8, page: 15, width: 229.1 },
};

/**
 * Contrato PROTEÇÃO + LIMPEZA — overrides onde o layout diverge do normal
 * (Parágrafo Quinto de limpeza desloca o conteúdo a partir da página 2).
 */
const coordenadasContratoLimpeza: Record<string, CoordCampo> = {
  ...coordenadasContratoNormal,
  cpf_cnpj: { x: 136, y: 665.8, page: 1 },
  marca_paineis: { x: 128.5, y: 536.4, page: 2 },
  modelo_paineis: { x: 232, y: 536.4, page: 2 },
  quantidade_paineis: { x: 103, y: 507.6, page: 2 },
  marca_inversores: { x: 141, y: 488.8, page: 2 },
  modelo_inversores: { x: 293, y: 488.8, page: 2 },
  quantidade_inversores: { x: 103, y: 460.0, page: 2 },
  marca_estrutura: { x: 150, y: 441.2, page: 2 },
  modelo_estrutura: { x: 293, y: 441.2, page: 2 },
  quantidade_estrutura: { x: 103, y: 412.4, page: 2 },
  valor_seguro: { x: 75, y: 601.1, page: 4, width: 110 },
  forma_pagamento: { x: 217, y: 601.1, page: 4, width: 213 },
  data_local: { x: 259, y: 678.4, page: 15, width: 139 },
  nome_cliente_assinatura: { x: 190.35, y: 487.8, page: 15, width: 229.1 },
  cpf_cnpj_assinatura: { x: 190.35, y: 473.8, page: 15, width: 229.1 },
};

function coordenadasParaTemplate(templatePath: string): Record<string, CoordCampo> {
  return templatePath === CONTRATO_PDF_LIMPEZA
    ? coordenadasContratoLimpeza
    : coordenadasContratoNormal;
}

export async function preencherPdfContrato(dados: DadosContrato): Promise<Blob> {
  try {
    console.log('🔄 Iniciando preenchimento do PDF do contrato...');
    console.log('📋 Dados recebidos:', {
      cliente: dados.cliente,
      equipamentos_count: dados.equipamentos?.length || 0,
      orcamento: dados.orcamento,
      plano: dados.plano
    });
    
    // Carregar o PDF template (limpeza ou padrão conforme o plano)
    const templatePath = resolverTemplateContrato(dados.plano);
    const coordenadasContrato = coordenadasParaTemplate(templatePath);
    console.log('📄 Template do contrato:', templatePath, {
      inclui_limpeza: planoIncluiLimpeza(dados.plano),
    });
    const { data: templateBlob, error: templateError } = await supabase.storage
      .from(CONTRATO_TEMPLATES_BUCKET)
      .download(templatePath);
    if (templateError || !templateBlob) {
      throw new Error(
        `Erro ao carregar template do contrato '${templatePath}' do bucket '${CONTRATO_TEMPLATES_BUCKET}': ${templateError?.message ?? 'arquivo não encontrado'}`
      );
    }
    const templateBytes = await templateBlob.arrayBuffer();
    
    // Carregar o PDF
    const pdfDoc = await PDFDocument.load(templateBytes);
    const pages = pdfDoc.getPages();
    
    console.log(`📄 PDF carregado com ${pages.length} páginas`);
    
    // Verificar dimensões de cada página para debug
    pages.forEach((page, index) => {
      const { width, height } = page.getSize();
      console.log(`📐 Página ${index + 1}: width=${width.toFixed(2)}, height=${height.toFixed(2)}`);
    });
    
    // Carregar fontes
    // Helvetica 12 tem proporções próximas ao corpo do template (Noto Sans 12)
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontSize = 12;
    
    // Calcular informações dos equipamentos
    const equipamentos = dados.equipamentos || [];
    const paineis = equipamentos.filter(eq => eq.equipamento?.tipo?.toLowerCase() === 'painel');
    const inversores = equipamentos.filter(eq => eq.equipamento?.tipo?.toLowerCase() === 'inversor');
    const estruturas = equipamentos.filter(eq => eq.equipamento?.tipo?.toLowerCase() === 'estrutura');
    
    const quantidadePaineis = paineis.reduce((total, eq) => total + (eq.quantidade || 0), 0);
    const quantidadeInversores = inversores.reduce((total, eq) => total + (eq.quantidade || 0), 0);
    const quantidadeEstrutura = estruturas.reduce((total, eq) => total + (eq.quantidade || 0), 0);
    
    const marcaPaineis = paineis[0]?.equipamento?.marca || 'Não informado';
    const modeloPaineis = paineis[0]?.equipamento?.modelo || 'Não informado';
    const potenciaPaineis = paineis[0]?.equipamento?.potencia || 'Não informado';
    const potenciaTotal = paineis.reduce((total, eq) => {
      const potencia = parseFloat(eq.equipamento?.potencia?.replace(/[^\d,.]/g, '').replace(',', '.') || '0');
      return total + (potencia * (eq.quantidade || 0));
    }, 0);
    
    const marcaInversores = inversores[0]?.equipamento?.marca || 'Não informado';
    const modeloInversores = inversores[0]?.equipamento?.modelo || 'Não informado';
    
    const marcaEstrutura = estruturas[0]?.equipamento?.marca || 'Não informado';
    const modeloEstrutura = estruturas[0]?.equipamento?.modelo || 'Não informado';
    
    // Formatar valores
    const formatarMoeda = (valor: number) => {
      return new Intl.NumberFormat('pt-BR', { 
        style: 'currency', 
        currency: 'BRL' 
      }).format(valor);
    };
    
    const formatarData = (data: string) => {
      if (!data) return '';
      return new Date(data).toLocaleDateString('pt-BR');
    };
    
    // Data por extenso: "Maringá, 26 de outubro de 2026."
    const partesDataLocal = (data: Date) => {
      const meses = [
        'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];
      return {
        dia: data.getDate().toString().padStart(2, '0'),
        mes: meses[data.getMonth()],
        ano: String(data.getFullYear()),
      };
    };
    
    // Função para mapear forma de pagamento
    const mapearFormaPagamento = async (formaPagamentoId: string | undefined): Promise<string> => {
      if (!formaPagamentoId) return '';
      
      try {
        // Tentar buscar o nome da forma de pagamento do banco
        const { data: formaPagamento } = await supabase
          .from('formas_pagamento')
          .select('nome')
          .eq('id', formaPagamentoId)
          .single();
        
        if (formaPagamento?.nome) {
          return formaPagamento.nome;
        }
      } catch (error) {
        console.warn('⚠️ Não foi possível buscar forma de pagamento, usando mapeamento padrão:', error);
      }
      
      // Fallback para mapeamento padrão
      const mapeamentoPadrao: Record<string, string> = {
        '1': 'À Vista',
        '2': 'Parcelado',
        'pix': 'PIX',
        'boleto-avista': 'Boleto à Vista',
        'boleto-parcelado': 'Boleto Parcelado',
        'cartao': 'Cartão de Crédito',
      };
      
      return mapeamentoPadrao[formaPagamentoId] || formaPagamentoId;
    };
    
    const { dia: dataDia, mes: dataMes, ano: dataAno } = partesDataLocal(new Date());
    
    // Mapear forma de pagamento antes de criar o array de campos
    const formaPagamentoTexto = await mapearFormaPagamento(dados.orcamento?.forma_pagamento);
    
    // Preparar dados para preenchimento
    const nomeCliente = dados.cliente.nome || '';
    const cpfCnpj = dados.cliente.cpf_cnpj || '';
    
    console.log('👤 Dados do cliente preparados:', {
      nome: nomeCliente,
      cpf_cnpj: cpfCnpj
    });
    
    const campos = [
      // Página 1 - Dados do cliente
      { 
        text: nomeCliente, 
        coordKey: 'nome_cliente' as const 
      },
      { 
        text: cpfCnpj, 
        coordKey: 'cpf_cnpj' as const 
      },
      
      // Página 2 - Dados do equipamento
      // Painel
      { 
        text: quantidadePaineis.toString(), 
        coordKey: 'quantidade_paineis' as const 
      },
      { 
        text: marcaPaineis, 
        coordKey: 'marca_paineis' as const 
      },
      { 
        text: modeloPaineis, 
        coordKey: 'modelo_paineis' as const 
      },
      // Inversor
      { 
        text: quantidadeInversores.toString(), 
        coordKey: 'quantidade_inversores' as const 
      },
      { 
        text: marcaInversores, 
        coordKey: 'marca_inversores' as const 
      },
      { 
        text: modeloInversores, 
        coordKey: 'modelo_inversores' as const 
      },
      // Estrutura
      { 
        text: quantidadeEstrutura.toString(), 
        coordKey: 'quantidade_estrutura' as const 
      },
      { 
        text: marcaEstrutura, 
        coordKey: 'marca_estrutura' as const 
      },
      { 
        text: modeloEstrutura, 
        coordKey: 'modelo_estrutura' as const 
      },
      
      // Página 4 - Dados do orçamento
      { 
        text: formatarMoeda(dados.orcamento?.valor_seguro || dados.contrato?.valor || dados.orcamento?.valor_total || 0), 
        coordKey: 'valor_seguro' as const 
      },
      { 
        text: formaPagamentoTexto, 
        coordKey: 'forma_pagamento' as const 
      },
      
      // Página 15 - "Maringá, 26 de outubro de 2026." / dados do CONTRATANTE sob a assinatura
      { text: `${dataDia} de ${dataMes} de ${dataAno}`, coordKey: 'data_local' as const },
      { text: nomeCliente, coordKey: 'nome_cliente_assinatura' as const },
      { text: cpfCnpj ? `CPF/CNPJ: ${cpfCnpj}` : '', coordKey: 'cpf_cnpj_assinatura' as const },
    ];
    
    console.log('📝 Preenchendo campos no PDF do contrato...');
    console.log(`📊 Total de campos para preencher: ${campos.length}`);
    
    // Preencher cada campo na página correta - usando o mesmo padrão da proposta comercial
    campos.forEach(campo => {
      if (!campo.text && campo.text !== '0') return; // Permitir zero como valor válido
      const coords = coordenadasContrato[campo.coordKey];
      if (!coords) {
        console.warn(`⚠️ Coordenadas ausentes para '${campo.coordKey}'`);
        return;
      }
      const pageIndex = (coords.page ?? 1) - 1;
      const page = pages[pageIndex] || pages[0];
      
      if (!page) {
        console.warn(`⚠️ Página ${coords.page ?? 1} não encontrada para '${campo.coordKey}'`);
        return;
      }
      
      // Obter dimensões da página para debug
      const { width, height } = page.getSize();
      
      // Verificar se as coordenadas estão dentro dos limites
      if (coords.x < 0 || coords.x > width || coords.y < 0 || coords.y > height) {
        console.warn(`⚠️ Coordenadas fora dos limites para '${campo.coordKey}': x=${coords.x.toFixed(2)}, y=${coords.y.toFixed(2)}, width=${width.toFixed(2)}, height=${height.toFixed(2)}`);
      }
      
      try {
        let drawX = coords.x;
        let size = fontSize;
        if (coords.width != null && coords.width > 0) {
          let textWidth = font.widthOfTextAtSize(campo.text, size);
          if (textWidth > coords.width) {
            size = Math.max(8, size * (coords.width / textWidth));
            textWidth = font.widthOfTextAtSize(campo.text, size);
          }
          drawX = coords.x + (coords.width - textWidth) / 2;
        }
        page.drawText(campo.text, {
          x: drawX,
          y: coords.y,
          size,
          font: font,
          color: rgb(0, 0, 0),
        });
        console.log(`✅ Campo '${campo.coordKey}' (página ${pageIndex + 1}, x:${drawX.toFixed(2)}, y:${coords.y.toFixed(2)}) preenchido com: "${campo.text}"`);
      } catch (error) {
        console.error(`❌ Erro ao preencher campo '${campo.coordKey}':`, error);
      }
    });
    
    console.log(`✅ Total de campos preenchidos: ${campos.length}`);
    
    // Salvar o PDF preenchido
    const pdfBytes = await pdfDoc.save();
    
    console.log('✅ PDF do contrato preenchido com sucesso!');
    
    // Garantir compatibilidade de tipo para BlobPart
    const pdfArrayBuffer = pdfBytes instanceof Uint8Array
      ? (pdfBytes.buffer as ArrayBuffer).slice(0)
      : (pdfBytes as unknown as ArrayBuffer);
    return new Blob([pdfArrayBuffer], { type: 'application/pdf' });
    
  } catch (error) {
    console.error('❌ Erro ao preencher PDF do contrato:', error);
    throw new Error(`Erro ao preencher PDF do contrato: ${error}`);
  }
}

// Função para fazer download do PDF preenchido
export async function baixarPdfContratoPreenchido(dados: DadosContrato) {
  try {
    const pdfBlob = await preencherPdfContrato(dados);
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${dados.cliente.nome.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Erro ao baixar PDF do contrato:', error);
    throw error;
  }
}

